export const mapSapDataToProductionLotPayload = (sapData, assignments = []) => {
  // First, collect all production tasks from the SAP data (from both root and confirmation groups)
  const allProductionTasks = [];

  // Add production tasks from root level if they exist
  if (sapData.productionTasks && Array.isArray(sapData.productionTasks)) {
    allProductionTasks.push(...sapData.productionTasks);
  }

  // Add production tasks from confirmation groups if they exist
  if (sapData.confirmationGroups && Array.isArray(sapData.confirmationGroups)) {
    sapData.confirmationGroups.forEach(group => {
      if (group.productionTasks && Array.isArray(group.productionTasks)) {
        allProductionTasks.push(...group.productionTasks);
      }
    });
  }

  // Remove duplicates based on productionTaskUUID or productionTaskId
  const uniqueProductionTasks = allProductionTasks.filter((task, index, self) => 
    index === self.findIndex(t => 
      t.productionTaskUUID === task.productionTaskUUID || 
      t.productionTaskId === task.productionTaskId
    )
  );

  return {
    lot: {
      // Basic Lot Information
      productionLotId: sapData.productionLotId || null,
      productionLotUUID: sapData.productionLotUUID || null,
      productionOrderId: sapData.productionOrderId || null,
      productionOrderUUID: sapData.productionOrderUUID || null,
      
      // Product Information
      mainOutputProductId: sapData.mainOutputProductId || null,
      mainOutputProductDescription: sapData.mainOutputProductDescription || null,
      
      // Site Information
      siteId: sapData.siteId || null,
      siteDescription: sapData.siteDescription || null,
      
      // Dates
      productionStartDate: sapData.productionStartDate || null,
      productionEndDate: sapData.productionEndDate || null,
      
      // Quantities
      totalQuantity: sapData.totalQuantity ?? 0,
      totalQuantityUnit: sapData.totalQuantityUnit || null,
      totalCompletedQuantity: sapData.totalCompletedQuantity ?? 0,
      totalCompletedQuantityUnit: sapData.totalCompletedQuantityUnit || null,
      
      // Status Codes
      lifeCycleStatusCode: sapData.lifeCycleStatusCode || null,
      lifeCycleStatusDescription: sapData.lifeCycleStatusDescription || null,
      activityProcessStatusCode: sapData.activityProcessStatusCode || null,
      reportingPointProcessingStatusCode: sapData.reportingPointProcessingStatusCode || null,
      productionOrderLifeCycleStatusCode: sapData.productionOrderLifeCycleStatusCode || null,
      productionOrderReleaseStatusCode: sapData.productionOrderReleaseStatusCode || null,
      
      // Timestamps
      createdAt: sapData.createdAt || new Date().toISOString(),
      updatedAt: sapData.updatedAt || new Date().toISOString(),
      
      // Confirmation Groups - WITHOUT productionTasks
      confirmationGroups: (sapData.confirmationGroups || []).map(group => ({
        confirmationGroupId: group.confirmationGroupId || null,
        confirmationGroupUUID: group.confirmationGroupUUID || null,
        lifeCycleStatusCode: group.lifeCycleStatusCode || null,
        processorObjectTypeCode: group.processorObjectTypeCode || null,
        productionLotId: sapData.productionLotId || null,
        productionLot: null,
        
        // Activities within this confirmation group
        activities: (group.activities || []).map(activity => ({
          activityId: activity.activityId || null,
          activityUUID: activity.activityUUID || null,
          activityDescription: activity.activityDescription || null,
          categoryCode: activity.categoryCode || null,
          operationId: activity.operationId || null,
          operationUUID: activity.operationUUID || null,
          operationTypeCode: activity.operationTypeCode || null,
          operationActivityTypeCode: activity.operationActivityTypeCode || null,
          plannedDuration: activity.plannedDuration || null,
          openDuration: activity.openDuration || null,
          totalConfirmedDuration: activity.totalConfirmedDuration || null,
          startDateTime: activity.startDateTime || null,
          endDateTime: activity.endDateTime || null,
          confirmMainResourceId: activity.confirmMainResourceId || null,
          workCentreId: activity.workCentreId || null,
          lifeCycleStatusCode: activity.lifeCycleStatusCode || null,
          processingStatusCode: activity.processingStatusCode || null,
          confirmationCompletedIndicator: activity.confirmationCompletedIndicator || false,
          confirmLogisticsAreaId: activity.confirmLogisticsAreaId || null,
          confirmationMethodCode: activity.confirmationMethodCode || null,
          confirmQuantity: activity.confirmQuantity ?? 0,
          confirmQuantityUnit: activity.confirmQuantityUnit || null,
          lineItemGroupId: activity.lineItemGroupId || null,
          confirmationGroupUUID: group.confirmationGroupUUID || null,
          confirmationGroupId: group.confirmationGroupId || null,
          confirmationGroup: null
        })),
        
        // Material Inputs
        materialInputs: (group.materialInputs || []).map(input => ({
          materialId: input.materialId || null,
          materialUUID: input.materialUUID || null,
          productId: input.productId || null,
          productDescription: input.productDescription || null,
          productTypeCode: input.productTypeCode || null,
          productCategoryCode: input.productCategoryCode || null,
          plannedQuantity: input.plannedQuantity ?? 0,
          plannedQuantityUnit: input.plannedQuantityUnit || null,
          openPlannedQuantity: input.openPlannedQuantity ?? 0,
          totalConfirmedQuantity: input.totalConfirmedQuantity ?? 0,
          confirmQuantity: input.confirmQuantity ?? 0,
          confirmQuantityUnit: input.confirmQuantityUnit || null,
          logisticsAreaId: input.logisticsAreaId || null,
          sourceLogisticsAreaId: input.sourceLogisticsAreaId || null,
          supplyPlanningAreaId: input.supplyPlanningAreaId || null,
          activityId: input.activityId || null,
          activityType: input.activityType || null,
          operationId: input.operationId || null,
          siteDescription: input.siteDescription || null,
          processingStatusCode: input.processingStatusCode || null,
          confirmationMethodCode: input.confirmationMethodCode || null,
          lineItemGroupId: input.lineItemGroupId || null,
          plannedIndicator: input.plannedIndicator ?? true,
          confirmationFinished: input.confirmationFinished ?? false,
          isInput: input.isInput ?? true,
          serialNumberAssignmentIndicator: input.serialNumberAssignmentIndicator ?? false,
          identifiedStockAssignmentIndicator: input.identifiedStockAssignmentIndicator ?? false,
          confirmationGroupId: group.confirmationGroupId || null,
          confirmationGroup: null
        })),
        
        // Material Outputs
        materialOutputs: (group.materialOutputs || []).map(output => ({
          materialId: output.materialId || null,
          materialUUID: output.materialUUID || null,
          productId: output.productId || null,
          productDescription: output.productDescription || null,
          productTypeCode: output.productTypeCode || null,
          productCategoryCode: output.productCategoryCode || null,
          plannedQuantity: output.plannedQuantity ?? 0,
          plannedQuantityUnit: output.plannedQuantityUnit || null,
          openPlannedQuantity: output.openPlannedQuantity ?? 0,
          totalConfirmedQuantity: output.totalConfirmedQuantity ?? 0,
          confirmQuantity: output.confirmQuantity ?? 0,
          confirmQuantityUnit: output.confirmQuantityUnit || null,
          logisticsAreaId: output.logisticsAreaId || null,
          sourceLogisticsAreaId: output.sourceLogisticsAreaId || null,
          supplyPlanningAreaId: output.supplyPlanningAreaId || null,
          activityId: output.activityId || null,
          activityType: output.activityType || null,
          operationId: output.operationId || null,
          siteDescription: output.siteDescription || null,
          processingStatusCode: output.processingStatusCode || null,
          confirmationMethodCode: output.confirmationMethodCode || null,
          lineItemGroupId: output.lineItemGroupId || null,
          plannedIndicator: output.plannedIndicator ?? true,
          confirmationFinished: output.confirmationFinished ?? false,
          isInput: output.isInput ?? false,
          serialNumberAssignmentIndicator: output.serialNumberAssignmentIndicator ?? false,
          identifiedStockAssignmentIndicator: output.identifiedStockAssignmentIndicator ?? false,
          confirmationGroupId: group.confirmationGroupId || null,
          confirmationGroup: null
        }))
        
        // ⚠️ IMPORTANT: DO NOT include productionTasks here
        // productionTasks: []  // ← REMOVED COMPLETELY
      })),
      
      // Production Tasks - at root level only, with duplicates removed
      productionTasks: uniqueProductionTasks.map(task => ({
        productionTaskId: task.productionTaskId || null,
        productionTaskUUID: task.productionTaskUUID || null,
        operationTypeCode: task.operationTypeCode || null,
        operationActivityTypeCode: task.operationActivityTypeCode || null,
        resourceUUID: task.resourceUUID || null,
        activityId: task.activityId || null,
        activityUUID: task.activityUUID || null,
        confirmMainResourceId: task.confirmMainResourceId || null,
        lifeCycleStatusCode: task.lifeCycleStatusCode || null,
        lifeCycleStatusDescription: task.lifeCycleStatusDescription || null,
        processingStatusCode: task.processingStatusCode || null,
        productionLotId: sapData.productionLotId || null,
        confirmationGroupUUID: task.confirmationGroupUUID || null,
        barcodeImage: task.barcodeImage || null,
        barcodeBase64: task.barcodeBase64 || null,
        createdAt: task.createdAt || null,
        updatedAt: task.updatedAt || null,
        productionLot: null,
        assignments: task.assignments || []
      }))
    },
    
    // Task Assignments - from the modal selection
    taskAssignments: assignments.map(assignment => ({
      productionTaskId: assignment.productionTaskId || null,
      productionTask: null,
      userId: assignment.userId || null,
      teamId: assignment.teamId || null
    }))
  };
};